import { Injectable } from '@angular/core';
import {
    DiscountsAndMultipliersService,
    IccDocumentPosition,
    OfferValidity,
    PriceVersion,
    Product,
    SalesDocument,
    SalesDocumentPosition,
    State,
    TaxRatesService,
} from '@icc/b2b/shared';
import { ComplexConfiguration } from '@icc/configurator/complex/configurations';
import { RollerShutterConfiguration } from '@icc/configurator/roller-shutter/configurations';
import { PricesDataService } from '@icc/shared/prices-data.service';
import { PositionService } from './position.service';
import { DocumentManagerService } from './document-manager.service';
import { changePosition } from './+state/sales-documents.actions';
import { Store } from '@ngrx/store';

type SlavePositionData = {
    product: Product;
    productPrice: any;
    productUnit: any;
    fields: {
        [fieldId: string]: number;
    };
};

@Injectable({
    providedIn: 'root',
})
export class DocumentSlavePositionsService {
    constructor(
        private pricesDataService: PricesDataService,
        private positionService: PositionService,
        private documentManagerService: DocumentManagerService,
        private discountsAndMultipliersService: DiscountsAndMultipliersService,
    ) {}

    async updateSlavePositions(
        position: SalesDocumentPosition,
        activeDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        positions: SalesDocumentPosition[]
    ) {
        if (
            position.configuration.type === 'complex' &&
            ComplexConfiguration.is(position.configuration)
        ) {
            position.slaves = {};
            const productPrices = await this.pricesDataService.getProductPricesForPriceVersion(
                documentPriceVersion.id,
                activeDocument.buyerCompanyId
            );
            const productFields = Object.values(position.configuration.fields).filter(
                (el) => el.type === 'select_product' && el.selectedProductId
            );

            const productsMap = new Map<number, Map<null | string, SlavePositionData>>();

            for (const productField of productFields) {
                const quantityField = position.configuration.fields[productField.id + '_qty'];
                const unlinkedFieldId = productField.productUnlinked ? productField.id : null;

                if (Number(quantityField?.value) < 1) {
                    continue;
                }

                if (!productsMap.get(productField.selectedProductId)?.get(unlinkedFieldId)) {
                    let productMap = productsMap.get(productField.selectedProductId);
                    if (!productMap) {
                        productMap = new Map<null | string, SlavePositionData>();
                        productsMap.set(productField.selectedProductId, productMap);
                    }

                    const product = await this.positionService.getProduct(
                        productField.selectedProductId
                    );
                    const productPrice = productPrices[productField.selectedProductId];
                    const productUnit = productPrice?.priceUnits?.find((el) => el.isPrimary);
                    productMap.set(unlinkedFieldId, {
                        product,
                        productPrice,
                        productUnit,
                        fields: {
                            [productField.id]: Number(quantityField?.value || 0),
                        },
                    });
                } else {
                    productsMap.get(productField.selectedProductId).get(unlinkedFieldId).fields[productField.id] =
                        Number(quantityField?.value || 0);
                }
            }
            await this.createOrUpdateSlavePositions({
                masterPosition: position,
                activeDocument,
                productsMap,
                positions,
                documentPriceVersion,
                currentOfferValidity,
            });
            this.documentManagerService.savePosition(position.uuid, activeDocument.uuid, {
                slaves: position.slaves,
            });
        }

        if (
            position.configuration.type === 'roller_shutter' &&
            RollerShutterConfiguration.is(position.configuration)
        ) {
            await this.updateAllRollerShutterPackagingSlavePositions(
                activeDocument,
                documentPriceVersion,
                currentOfferValidity,
                positions
            );
        }
    }

    async updateSlavePositionsOnDelete(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity
    ) {
        if (
            masterPosition.configuration.type === 'complex' &&
            ComplexConfiguration.is(masterPosition.configuration) &&
            Object.keys(masterPosition.slaves || {}).length > 0
        ) {
            const positions = await this.documentManagerService.getDocumentPosition(
                activeDocument.uuid
            );
            await this.removeSlavePositions(masterPosition, activeDocument, positions);
        }

        if (
            masterPosition.configuration.type === 'roller_shutter' &&
            RollerShutterConfiguration.is(masterPosition.configuration)
        ) {
            const positions = await this.documentManagerService.getDocumentPosition(
                activeDocument.uuid
            );

            await this.updateAllRollerShutterPackagingSlavePositions(
                activeDocument,
                documentPriceVersion,
                currentOfferValidity,
                positions as SalesDocumentPosition[]
            );
        }
    }

    async updateSlavePositionQuantity(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument,
        oldPositionQuantity: number,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity
    ) {
        if (
            masterPosition.configuration.type === 'complex' &&
            ComplexConfiguration.is(masterPosition.configuration) &&
            Object.keys(masterPosition.slaves || {}).length > 0
        ) {
            const positions = await this.documentManagerService.getDocumentPosition(
                activeDocument.uuid
            );
            const slavePositions = positions.filter(
                (el) => el.type === 'static' && (el.masters || {})[masterPosition.uuid]
            );

            for (const slavePosition of slavePositions) {
                const quantity = this.sumFieldsQuantity(slavePosition.masters[masterPosition.uuid]);
                slavePosition.quantity = slavePosition.quantity - quantity * oldPositionQuantity + quantity * masterPosition.quantity;
                this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
                    quantity: slavePosition.quantity,
                });

                if (activeDocument.discountType === 'value' && slavePosition.type !== 'defect') {
                    await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(activeDocument, slavePosition, 'sales');
                    this.documentManagerService.savePositionPrices(
                        slavePosition.uuid,
                        activeDocument.uuid,
                        {
                            priceTaxRateId: slavePosition.priceTaxRateId,
                            priceTaxRate: slavePosition.priceTaxRate,
                            priceBeforePromotionsValue: slavePosition.priceBeforePromotionsValue,
                            pricePromotions: slavePosition.pricePromotions,
                            priceBeforeDiscountsValue: slavePosition.priceBeforeDiscountsValue,
                            priceDiscounts: slavePosition.priceDiscounts,
                            priceAfterDiscountsValue: slavePosition.priceAfterDiscountsValue,
                            priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                            priceCurrency: slavePosition.priceCurrency,
                        },
                    );
                }
            }
        }

        if (
            masterPosition.configuration.type === 'roller_shutter' &&
            RollerShutterConfiguration.is(masterPosition.configuration)
        ) {
            const positions = await this.documentManagerService.getDocumentPosition(
                activeDocument.uuid
            );

            await this.updateAllRollerShutterPackagingSlavePositions(
                activeDocument,
                documentPriceVersion,
                currentOfferValidity,
                positions as SalesDocumentPosition[]
            );
        }
    }

    private async createOrUpdateSlavePositions({
        masterPosition,
        activeDocument,
        productsMap,
        positions,
        documentPriceVersion,
        currentOfferValidity,
    }: {
        masterPosition: SalesDocumentPosition;
        activeDocument: SalesDocument;
        productsMap: Map<number, Map<null | string, SlavePositionData>>;
        positions: SalesDocumentPosition[];
        documentPriceVersion: PriceVersion;
        currentOfferValidity: OfferValidity;
    }) {
        masterPosition.slaves = {};
        for (const [productId, productMap] of productsMap) {
            for (const [unlinkedFieldId, productData] of productMap) {
                let slavePosition = !unlinkedFieldId && positions.find(
                    (el) =>
                        el.type === 'static' &&
                        el.configuration.productId === productId &&
                        el.priceUnitId === productData.productUnit?.id &&
                        el.priceUnitCode === productData.productUnit?.code &&
                        Object.keys(el.masters || {}).length > 0
                );
                if (slavePosition) {
                    await this.updateSlavePosition(
                        masterPosition,
                        activeDocument,
                        slavePosition,
                        productData
                    );
                } else {
                    slavePosition = await this.createSlavePosition(
                        !unlinkedFieldId ? masterPosition : null,
                        activeDocument,
                        productData,
                        documentPriceVersion,
                        currentOfferValidity
                    );
                }

                if (!unlinkedFieldId && slavePosition) {
                    for (const fieldId in productData.fields) {
                        if (Object.prototype.hasOwnProperty.call(productData.fields, fieldId)) {
                            masterPosition.slaves[fieldId] = {
                                positionId: slavePosition.uuid,
                                productId,
                                unitId: productData.productUnit?.id,
                                unitCode: productData.productUnit?.code,
                            };
                        }
                    }
                }
            }
        }

        await this.removeSlavePositions(masterPosition, activeDocument, positions, productsMap);
    }

    private async createSlavePosition(
        masterPosition: SalesDocumentPosition | null,
        activeDocument: SalesDocument,
        productData: SlavePositionData,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity
    ) {
        let quantity = 0;
        for (const fieldId in productData.fields) {
            if (Object.prototype.hasOwnProperty.call(productData.fields, fieldId)) {
                quantity += productData.fields[fieldId];
            }
        }

        if (quantity < 1) {
            return null;
        }

        const unitInfo = this.pricesDataService.getUnitInfo(productData.productUnit?.id, productData.productUnit?.code);

        const slavePosition = await this.positionService.createPositionFromProduct(
            activeDocument,
            activeDocument.priceCurrency,
            productData.product,
            {
                price: {
                    value: productData.productUnit?.value,
                    currency: productData.productPrice?.currency,
                    isGross: productData.productPrice?.isGross,
                    taxRate: productData.productPrice?.taxRate,
                    resetTaxRate: productData.productPrice?.resetTaxRate,
                    unitId: productData.productUnit?.id,
                    unitCode: productData.productUnit?.code,
                    revision: productData.productPrice?.revision || 0,
                },
                quantity: quantity * (masterPosition?.quantity ?? 1),
                unit: {
                    id: productData.productUnit?.id,
                    symbol: unitInfo?.symbol,
                    code: productData.productUnit?.code,
                },
            },
            documentPriceVersion,
            currentOfferValidity,
            await this.documentManagerService.getDocumentPosition(activeDocument.uuid) as SalesDocumentPosition[],
            masterPosition ? {
                positionId: masterPosition.uuid,
                fields: productData.fields,
            } : null
        );
        this.documentManagerService.addPosition(slavePosition, activeDocument);
        return slavePosition;
    }

    private async updateSlavePosition(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument,
        slavePosition: SalesDocumentPosition,
        productData: SlavePositionData
    ) {
        const quantity = this.sumFieldsQuantity(productData.fields);
        const oldQuantity = this.sumFieldsQuantity(slavePosition.masters[masterPosition.uuid]);
        slavePosition.quantity =
            slavePosition.quantity -
            oldQuantity * masterPosition.quantity +
            quantity * masterPosition.quantity;
        slavePosition.masters[masterPosition.uuid] = productData.fields;
        this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
            masters: slavePosition.masters,
            quantity: slavePosition.quantity,
        }, false);
        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(activeDocument, slavePosition, 'sales');
        this.documentManagerService.savePositionPrices(
            slavePosition.uuid,
            activeDocument.uuid,
            {
                priceTaxRateId: slavePosition.priceTaxRateId,
                priceTaxRate: slavePosition.priceTaxRate,
                priceBeforePromotionsValue: slavePosition.priceBeforePromotionsValue,
                pricePromotions: slavePosition.pricePromotions,
                priceBeforeDiscountsValue: slavePosition.priceBeforeDiscountsValue,
                priceDiscounts: slavePosition.priceDiscounts,
                priceAfterDiscountsValue: slavePosition.priceAfterDiscountsValue,
                priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                priceCurrency: slavePosition.priceCurrency,
            },
        );

    }

    private async removeSlavePositions(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument,
        positions: IccDocumentPosition[],
        productsMap?: Map<number, Map<null | string, SlavePositionData>>
    ) {
        const oldSlavePositions = positions.filter(
            (el) => el.type === 'static' && (el.masters || {})[masterPosition.uuid]
        ) as SalesDocumentPosition[];

        for (const slavePosition of oldSlavePositions) {
            if (
                productsMap &&
                productsMap.get(slavePosition.configuration?.productId)?.get(null)?.productUnit?.id ===
                    slavePosition.priceUnitId &&
                productsMap.get(slavePosition.configuration?.productId)?.get(null)?.productUnit?.code ===
                    slavePosition.priceUnitCode
            ) {
                continue;
            }

            const quantity = this.sumFieldsQuantity(slavePosition.masters[masterPosition.uuid]);
            slavePosition.quantity = slavePosition.quantity - quantity * masterPosition.quantity;
            delete slavePosition.masters[masterPosition.uuid];
            if (Object.keys(slavePosition.masters).length === 0 || slavePosition.quantity === 0) {
                this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
                    masters: slavePosition.masters,
                    isDeleted: true,
                }, false);
            } else {
                this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
                    masters: slavePosition.masters,
                    quantity: slavePosition.quantity,
                }, false);
                await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(activeDocument, slavePosition, 'sales');
                this.documentManagerService.savePositionPrices(
                    slavePosition.uuid,
                    activeDocument.uuid,
                    {
                        priceTaxRateId: slavePosition.priceTaxRateId,
                        priceTaxRate: slavePosition.priceTaxRate,
                        priceBeforePromotionsValue: slavePosition.priceBeforePromotionsValue,
                        pricePromotions: slavePosition.pricePromotions,
                        priceBeforeDiscountsValue: slavePosition.priceBeforeDiscountsValue,
                        priceDiscounts: slavePosition.priceDiscounts,
                        priceAfterDiscountsValue: slavePosition.priceAfterDiscountsValue,
                        priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                        priceCurrency: slavePosition.priceCurrency,
                    },
                );
            }
        }
    }

    private sumFieldsQuantity(fields: { [fieldId: string]: number }) {
        let quantity = 0;
        for (const fieldId in fields) {
            if (Object.prototype.hasOwnProperty.call(fields, fieldId)) {
                quantity += fields[fieldId];
            }
        }
        return quantity;
    }

    /**
     * Aktualizuje globalne opakowanie dla wszystkich pozycji rolet na dokumencie.
     * Agreguje prowadnice i skrzynki ze wszystkich pozycji i tworzy jedną pozycję zbierającą.
     * @param activeDocument - Dokument sprzedażowy
     * @param documentPriceVersion - Wersja cennika
     * @param currentOfferValidity - Ważność oferty
     * @param positions - Wszystkie pozycje na dokumencie
     */
    async updateAllRollerShutterPackagingSlavePositions(
        activeDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        positions: SalesDocumentPosition[]
    ) {
        const rollerPositionsWithPackaging = positions.filter(
            (pos) =>
                !pos.isDeleted &&
                pos.configuration?.type === 'roller_shutter' &&
                RollerShutterConfiguration.is(pos.configuration) &&
                pos.configuration.packaging?.id &&
                pos.configuration.packaging?.productId
        );

        if (rollerPositionsWithPackaging.length === 0) {
            await this.removeAllRollerShutterPackagingSlavePositions(activeDocument, positions);
            return;
        }

        const corrugatedBoardPositions = rollerPositionsWithPackaging.filter(
            (pos) => (pos.configuration as RollerShutterConfiguration).packaging?.packagingType === 'corrugated_board'
        );

        const rigidCardboardPositions = rollerPositionsWithPackaging.filter(
            (pos) => (pos.configuration as RollerShutterConfiguration).packaging?.packagingType === 'rigid_cardboard'
        );

        if (corrugatedBoardPositions.length > 0) {
            await this.processCorrugatedBoardPackaging(
                activeDocument,
                documentPriceVersion,
                currentOfferValidity,
                positions,
                corrugatedBoardPositions
            );
        }

        if (rigidCardboardPositions.length > 0) {
            await this.processRigidCardboardPackaging(
                activeDocument,
                documentPriceVersion,
                currentOfferValidity,
                positions,
                rigidCardboardPositions
            );
        }

        await this.removeOrphanedPackagingSlavePositions(
            activeDocument,
            positions,
            rollerPositionsWithPackaging
        );
    }

    private async processCorrugatedBoardPackaging(
        activeDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        allPositions: SalesDocumentPosition[],
        corrugatedBoardPositions: SalesDocumentPosition[]
    ) {
        interface AggregatedGuide {
            lengthMM: number;
            hasMosquitoSupport: boolean;
        }

        const allGuidesWithoutMosquito: AggregatedGuide[] = [];
        const allGuidesWithMosquito: AggregatedGuide[] = [];
        let totalBoxesLengthMM = 0;
        let totalBoxesCount = 0;

        const collectGuidesFromPosition = (config: RollerShutterConfiguration): AggregatedGuide[] => {
            const guides: AggregatedGuide[] = [];
            const rollerGuides = config.guides || [];
            const guidesExtensions = config.guidesExtensions || [];

            rollerGuides.forEach((guidesPair, index) => {
                const prevGuidesPair = rollerGuides[index - 1];

                // Znajdź wydłużenia dla tej pary prowadnic
                const extension = guidesExtensions.find(ext => ext.shutterId === guidesPair.shutterId);

                // Lewa prowadnica
                if (guidesPair.left) {
                    const leftGuide = guidesPair.left;
                    let lengthMM = leftGuide.length || 0;

                    // Dodaj wydłużenie lewej prowadnicy
                    if (extension && extension.left) {
                        lengthMM += extension.left;
                    }

                    // Sprawdź czy prowadnica jest współdzielona
                    const isSharedWithPrev =
                        index > 0 &&
                        prevGuidesPair?.right &&
                        leftGuide.doubleGuide &&
                        prevGuidesPair.right.doubleGuide &&
                        !leftGuide.normalDoubleGuide;

                    if (!isSharedWithPrev) {
                        guides.push({
                            lengthMM,
                            hasMosquitoSupport: leftGuide.withMosquitoChannel === true,
                        });
                    }
                }

                // Prawa prowadnica
                if (guidesPair.right) {
                    const rightGuide = guidesPair.right;
                    let lengthMM = rightGuide.length || 0;

                    // Dodaj wydłużenie prawej prowadnicy
                    if (extension && extension.right) {
                        lengthMM += extension.right;
                    }

                    guides.push({
                        lengthMM,
                        hasMosquitoSupport: rightGuide.withMosquitoChannel === true,
                    });
                }

                // Prowadnice moskitierowe
                if (guidesPair.mosquitoScreens && guidesPair.mosquitoScreens.length > 0) {
                    guidesPair.mosquitoScreens.forEach((mosquitoScreen) => {
                        if (mosquitoScreen.guide) {
                            const mosquitoGuide = mosquitoScreen.guide;
                            const lengthMM = mosquitoGuide.length || 0;

                            guides.push({
                                lengthMM,
                                hasMosquitoSupport: true,
                            });
                        }
                    });
                }
            });

            return guides;
        };

        // Zbierz wszystkie prowadnice i skrzynki ze wszystkich pozycji
        for (const position of corrugatedBoardPositions) {
            const config = position.configuration as RollerShutterConfiguration;
            const positionQuantity = position.quantity || 1;

            // Zbierz prowadnice z tej pozycji (pomnożone przez ilość pozycji)
            const guidesFromPosition = collectGuidesFromPosition(config);
            for (let i = 0; i < positionQuantity; i++) {
                guidesFromPosition.forEach((guide) => {
                    if (guide.hasMosquitoSupport) {
                        allGuidesWithMosquito.push(guide);
                    } else {
                        allGuidesWithoutMosquito.push(guide);
                    }
                });
            }

            const shutters = config.shutters || [];
            const boxLengthMM = shutters.reduce((sum, shutter) => sum + (shutter.width || 0), 0);

            const extensionsSum = 
                (config.boxWidthExtensionValues?.marginLeft || 0) +
                (config.boxWidthExtensionValues?.marginBetween1and2 || 0) +
                (config.boxWidthExtensionValues?.marginBetween2and3 || 0) +
                (config.boxWidthExtensionValues?.marginRight || 0);

            totalBoxesLengthMM += (boxLengthMM + extensionsSum) * positionQuantity;
            totalBoxesCount += positionQuantity;
        }

        const guidesWithoutMosquitoPackages = allGuidesWithoutMosquito.length > 0 
            ? Math.ceil(allGuidesWithoutMosquito.length / 12) 
            : 0;

        const guidesWithMosquitoPackages = allGuidesWithMosquito.length > 0 
            ? Math.ceil(allGuidesWithMosquito.length / 8) 
            : 0;

        const guidesWithoutMosquitoAvgMM =
            allGuidesWithoutMosquito.length > 0
                ? allGuidesWithoutMosquito.reduce((sum, g) => sum + g.lengthMM, 0) /
                  allGuidesWithoutMosquito.length
                : 0;

        const guidesWithMosquitoAvgMM =
            allGuidesWithMosquito.length > 0
                ? allGuidesWithMosquito.reduce((sum, g) => sum + g.lengthMM, 0) /
                  allGuidesWithMosquito.length
                : 0;

        let totalMB = 0;

        if (guidesWithoutMosquitoPackages > 0) {
            totalMB += (guidesWithoutMosquitoPackages * guidesWithoutMosquitoAvgMM) / 1000;
        }

        if (guidesWithMosquitoPackages > 0) {
            totalMB += (guidesWithMosquitoPackages * guidesWithMosquitoAvgMM) / 1000;
        }

        if (totalBoxesCount > 0) {
            totalMB += totalBoxesLengthMM / 1000;
        }

        totalMB = Math.round(totalMB * 100) / 100;

        if (totalMB < 0.001) {
            return;
        }

        const firstConfig = corrugatedBoardPositions[0].configuration as RollerShutterConfiguration;
        const productId = firstConfig.packaging.productId;

        const productPrices = await this.pricesDataService.getProductPricesForPriceVersion(
            documentPriceVersion.id,
            activeDocument.buyerCompanyId
        );

        const product = await this.positionService.getProductForSlavePosition(productId);
        const productPrice = productPrices[productId];
        const productUnit = productPrice?.priceUnits?.find((el) => el.isPrimary);

        const existingSlavePosition = allPositions.find(
            (pos) =>
                pos.type === 'static' &&
                pos.configuration?.productId === productId &&
                pos.masters &&
                Object.keys(pos.masters).some((masterUuid) =>
                    corrugatedBoardPositions.some((rp) => rp.uuid === masterUuid)
                )
        );

        if (existingSlavePosition) {
            existingSlavePosition.quantity = totalMB;
            existingSlavePosition.masters = {};

            corrugatedBoardPositions.forEach((masterPosition) => {
                existingSlavePosition.masters[masterPosition.uuid] = {
                    packaging: totalMB / corrugatedBoardPositions.length,
                };

                masterPosition.slaves = masterPosition.slaves || {};
                masterPosition.slaves['packaging'] = {
                    positionId: existingSlavePosition.uuid,
                    unitId: productUnit?.id,
                    unitCode: productUnit?.code,
                    productId: product.id,
                };

                this.documentManagerService.savePosition(
                    masterPosition.uuid,
                    activeDocument.uuid,
                    {
                        slaves: masterPosition.slaves,
                    },
                    false
                );
            });

            this.documentManagerService.savePosition(
                existingSlavePosition.uuid,
                activeDocument.uuid,
                {
                    masters: existingSlavePosition.masters,
                    quantity: existingSlavePosition.quantity,
                },
                false
            );

            await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                activeDocument,
                existingSlavePosition,
                'sales'
            );
            this.documentManagerService.savePositionPrices(
                existingSlavePosition.uuid,
                activeDocument.uuid,
                {
                    priceTaxRateId: existingSlavePosition.priceTaxRateId,
                    priceTaxRate: existingSlavePosition.priceTaxRate,
                    priceBeforePromotionsValue: existingSlavePosition.priceBeforePromotionsValue,
                    pricePromotions: existingSlavePosition.pricePromotions,
                    priceBeforeDiscountsValue: existingSlavePosition.priceBeforeDiscountsValue,
                    priceDiscounts: existingSlavePosition.priceDiscounts,
                    priceAfterDiscountsValue: existingSlavePosition.priceAfterDiscountsValue,
                    priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                    priceCurrency: existingSlavePosition.priceCurrency,
                }
            );
        } else {
            const unitInfo = this.pricesDataService.getUnitInfo(
                productUnit?.id,
                productUnit?.code
            );

            const slavePosition = await this.positionService.createPositionFromProduct(
                activeDocument,
                activeDocument.priceCurrency,
                product,
                {
                    price: {
                        value: productUnit?.value,
                        currency: productPrice?.currency,
                        isGross: productPrice?.isGross,
                        taxRate: productPrice?.taxRate,
                        resetTaxRate: productPrice?.resetTaxRate,
                        unitId: productUnit?.id,
                        unitCode: productUnit?.code,
                        revision: productPrice?.revision || 0,
                    },
                    quantity: totalMB,
                    unit: {
                        id: productUnit?.id,
                        symbol: unitInfo?.symbol,
                        code: productUnit?.code,
                    },
                },
                documentPriceVersion,
                currentOfferValidity,
                allPositions,
                null
            );

            slavePosition.masters = {};
            corrugatedBoardPositions.forEach((masterPosition) => {
                slavePosition.masters[masterPosition.uuid] = {
                    packaging: totalMB / corrugatedBoardPositions.length,
                };

                masterPosition.slaves = masterPosition.slaves || {};
                masterPosition.slaves['packaging'] = {
                    positionId: slavePosition.uuid,
                    unitId: productUnit?.id,
                    unitCode: productUnit?.code,
                    productId: product.id,
                };

                this.documentManagerService.savePosition(
                    masterPosition.uuid,
                    activeDocument.uuid,
                    {
                        slaves: masterPosition.slaves,
                    },
                    false
                );
            });

            this.documentManagerService.addPosition(slavePosition, activeDocument);
        }
    }

    private async processRigidCardboardPackaging(
        activeDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        allPositions: SalesDocumentPosition[],
        rigidCardboardPositions: SalesDocumentPosition[]
    ) {
        let totalBoxesLengthMM = 0;

        for (const position of rigidCardboardPositions) {
            const config = position.configuration as RollerShutterConfiguration;
            const positionQuantity = position.quantity || 1;

            const shutters = config.shutters || [];
            const boxLengthMM = shutters.reduce((sum, shutter) => sum + (shutter.width || 0), 0);

            const extensionsSum = 
                (config.boxWidthExtensionValues?.marginLeft || 0) +
                (config.boxWidthExtensionValues?.marginBetween1and2 || 0) +
                (config.boxWidthExtensionValues?.marginBetween2and3 || 0) +
                (config.boxWidthExtensionValues?.marginRight || 0);

            totalBoxesLengthMM += (boxLengthMM + extensionsSum) * positionQuantity;
        }

        let totalMB = totalBoxesLengthMM / 1000;
        totalMB = Math.round(totalMB * 100) / 100;

        if (totalMB < 0.001) {
            return;
        }

        const firstConfig = rigidCardboardPositions[0].configuration as RollerShutterConfiguration;
        const productId = firstConfig.packaging.productId;

        const productPrices = await this.pricesDataService.getProductPricesForPriceVersion(
            documentPriceVersion.id,
            activeDocument.buyerCompanyId
        );

        const product = await this.positionService.getProductForSlavePosition(productId);
        const productPrice = productPrices[productId];
        const productUnit = productPrice?.priceUnits?.find((el) => el.isPrimary);

        const existingSlavePosition = allPositions.find(
            (pos) =>
                pos.type === 'static' &&
                pos.configuration?.productId === productId &&
                pos.masters &&
                Object.keys(pos.masters).some((masterUuid) =>
                    rigidCardboardPositions.some((rp) => rp.uuid === masterUuid)
                )
        );

        if (existingSlavePosition) {
            existingSlavePosition.quantity = totalMB;
            existingSlavePosition.masters = {};

            rigidCardboardPositions.forEach((masterPosition) => {
                existingSlavePosition.masters[masterPosition.uuid] = {
                    packaging: totalMB / rigidCardboardPositions.length,
                };

                masterPosition.slaves = masterPosition.slaves || {};
                masterPosition.slaves['packaging'] = {
                    positionId: existingSlavePosition.uuid,
                    unitId: productUnit?.id,
                    unitCode: productUnit?.code,
                    productId: product.id,
                };

                this.documentManagerService.savePosition(
                    masterPosition.uuid,
                    activeDocument.uuid,
                    {
                        slaves: masterPosition.slaves,
                    },
                    false
                );
            });

            this.documentManagerService.savePosition(
                existingSlavePosition.uuid,
                activeDocument.uuid,
                {
                    masters: existingSlavePosition.masters,
                    quantity: existingSlavePosition.quantity,
                },
                false
            );

            await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                activeDocument,
                existingSlavePosition,
                'sales'
            );
            this.documentManagerService.savePositionPrices(
                existingSlavePosition.uuid,
                activeDocument.uuid,
                {
                    priceTaxRateId: existingSlavePosition.priceTaxRateId,
                    priceTaxRate: existingSlavePosition.priceTaxRate,
                    priceBeforePromotionsValue: existingSlavePosition.priceBeforePromotionsValue,
                    pricePromotions: existingSlavePosition.pricePromotions,
                    priceBeforeDiscountsValue: existingSlavePosition.priceBeforeDiscountsValue,
                    priceDiscounts: existingSlavePosition.priceDiscounts,
                    priceAfterDiscountsValue: existingSlavePosition.priceAfterDiscountsValue,
                    priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                    priceCurrency: existingSlavePosition.priceCurrency,
                }
            );
        } else {
            const unitInfo = this.pricesDataService.getUnitInfo(
                productUnit?.id,
                productUnit?.code
            );

            const slavePosition = await this.positionService.createPositionFromProduct(
                activeDocument,
                activeDocument.priceCurrency,
                product,
                {
                    price: {
                        value: productUnit?.value,
                        currency: productPrice?.currency,
                        isGross: productPrice?.isGross,
                        taxRate: productPrice?.taxRate,
                        resetTaxRate: productPrice?.resetTaxRate,
                        unitId: productUnit?.id,
                        unitCode: productUnit?.code,
                        revision: productPrice?.revision || 0,
                    },
                    quantity: totalMB,
                    unit: {
                        id: productUnit?.id,
                        symbol: unitInfo?.symbol,
                        code: productUnit?.code,
                    },
                },
                documentPriceVersion,
                currentOfferValidity,
                allPositions,
                null
            );

            slavePosition.masters = {};
            rigidCardboardPositions.forEach((masterPosition) => {
                slavePosition.masters[masterPosition.uuid] = {
                    packaging: totalMB / rigidCardboardPositions.length,
                };

                masterPosition.slaves = masterPosition.slaves || {};
                masterPosition.slaves['packaging'] = {
                    positionId: slavePosition.uuid,
                    unitId: productUnit?.id,
                    unitCode: productUnit?.code,
                    productId: product.id,
                };

                this.documentManagerService.savePosition(
                    masterPosition.uuid,
                    activeDocument.uuid,
                    {
                        slaves: masterPosition.slaves,
                    },
                    false
                );
            });

            this.documentManagerService.addPosition(slavePosition, activeDocument);
        }
    }

    private async removeOrphanedPackagingSlavePositions(
        activeDocument: SalesDocument,
        allPositions: SalesDocumentPosition[],
        currentRollerPositionsWithPackaging: SalesDocumentPosition[]
    ) {
        const usedPackagingProductIds = new Set<number>();
        currentRollerPositionsWithPackaging.forEach((pos) => {
            const config = pos.configuration as RollerShutterConfiguration;
            if (config.packaging?.productId) {
                usedPackagingProductIds.add(config.packaging.productId);
            }
        });

        const packagingSlavePositions = allPositions.filter(
            (pos) =>
                pos.type === 'static' &&
                pos.masters &&
                Object.values(pos.masters).some((master: any) => master && master.hasOwnProperty('packaging'))
        );

        for (const slavePosition of packagingSlavePositions) {
            const slaveProductId = slavePosition.configuration?.productId;

            if (slaveProductId && !usedPackagingProductIds.has(slaveProductId)) {
                for (const masterUuid in slavePosition.masters) {
                    const masterPosition = allPositions.find((p) => p.uuid === masterUuid);
                    if (masterPosition && masterPosition.slaves && masterPosition.slaves['packaging']) {
                        delete masterPosition.slaves['packaging'];
                        this.documentManagerService.savePosition(
                            masterPosition.uuid,
                            activeDocument.uuid,
                            {
                                slaves: masterPosition.slaves,
                            },
                            false
                        );
                    }
                }

                this.documentManagerService.savePosition(
                    slavePosition.uuid,
                    activeDocument.uuid,
                    {
                        isDeleted: true,
                    },
                    false
                );
            }
        }
    }

    private async removeAllRollerShutterPackagingSlavePositions(
        activeDocument: SalesDocument,
        positions: SalesDocumentPosition[]
    ) {
        const slavePositions = positions.filter(
            (pos) =>
                pos.type === 'static' &&
                pos.masters &&
                Object.values(pos.masters).some((master: any) => master && master.hasOwnProperty('packaging'))
        );

        for (const slavePosition of slavePositions) {
            for (const masterUuid in slavePosition.masters) {
                const masterPosition = positions.find((p) => p.uuid === masterUuid);
                if (masterPosition && masterPosition.slaves && masterPosition.slaves['packaging']) {
                    delete masterPosition.slaves['packaging'];
                    this.documentManagerService.savePosition(
                        masterPosition.uuid,
                        activeDocument.uuid,
                        {
                            slaves: masterPosition.slaves,
                        },
                        false
                    );
                }
            }

            this.documentManagerService.savePosition(
                slavePosition.uuid,
                activeDocument.uuid,
                {
                    isDeleted: true,
                },
                false
            );
        }
    }
}

